import React, { useState, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { uploadManager } from '@renderer/utils/uploadManager'
import cogoToast from '@successtar/cogo-toast'
import { Button } from '@renderer/components'
import { openExternalUrl } from '@renderer/lib/runtime'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo']
const MAX_FILES_SINGLE_SESSION = 10
const MAX_FILES_MULTIPLE_SESSIONS = 50
const UNRESTRICTED_EMAIL = 'bahelamilikbersama@gmail.com'

const isValidFileType = (file: File): boolean => {
  return [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES].includes(file.type)
}

const getFileType = (file: File): 'image' | 'video' => {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'image'
  return 'video'
}

interface SessionValidationResult {
  isValid: boolean
  projectId?: string
  error?: string
}

export const UploadPage: React.FC = () => {
  const { user } = useOutletContext<{ user: { email: string; isBahelaCollaborator?: boolean } }>()
  const [sessionId, setSessionId] = useState<string>('')
  
  const isUnrestrictedUser = user.email === UNRESTRICTED_EMAIL || user.isBahelaCollaborator === true
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isValidatingSession, setIsValidatingSession] = useState<boolean>(false)
  const [multipleSessionsMode, setMultipleSessionsMode] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [projectId, setProjectId] = useState<string>('')
  const [sessionValidation, setSessionValidation] = useState<SessionValidationResult | null>(null)
  const [validatedSessions, setValidatedSessions] = useState<Map<string, SessionValidationResult>>(
    new Map()
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const extractSessionIdFromFilename = (filename: string): string | null => {
    const match = filename.match(/dreambooth-([a-z0-9]+)-?/i)
    return match ? match[1] : null
  }

  const validateSingleSession = async (
    sessionIdToValidate: string
  ): Promise<SessionValidationResult> => {
    // For unrestricted users, skip validation entirely - no API call needed
    if (isUnrestrictedUser) {
      return {
        isValid: true,
        projectId: undefined // Session validation skipped for unrestricted users
      }
    }

    // For regular users, enforce validation
    try {
      const response = await fetch(
        `${process.env.VITE_API_URL}/api/sessions/${sessionIdToValidate}/owner`,
        {
          credentials: 'include'
        }
      )

      if (response.status === 404) {
        return { isValid: false, error: 'Session not found' }
      }

      if (response.status === 403) {
        return { isValid: false, error: 'Session does not belong to you' }
      }

      if (response.status === 401) {
        return { isValid: false, error: 'Unauthorized' }
      }

      if (!response.ok) {
        return { isValid: false, error: 'Failed to validate session' }
      }

      const data = await response.json()
      return {
        isValid: true,
        projectId: data.projectId
      }
    } catch (error) {
      console.error('Error validating session:', error)
      return { isValid: false, error: 'Failed to validate session' }
    }
  }

  const validateMultipleSessions = async (
    sessionIds: string[]
  ): Promise<Map<string, SessionValidationResult>> => {
    const results = new Map<string, SessionValidationResult>()
    const errors: string[] = []

    // Validate all sessions in parallel
    const validationPromises = sessionIds.map(async (sid) => {
      const result = await validateSingleSession(sid)
      results.set(sid, result)
      if (!result.isValid) {
        errors.push(`Session ${sid}: ${result.error || 'Invalid'}`)
      }
      return result
    })

    await Promise.all(validationPromises)

    // Check if all sessions belong to the same project
    const validResults = Array.from(results.values()).filter((r) => r.isValid)
    if (validResults.length > 0) {
      const firstProjectId = validResults[0].projectId
      const allSameProject = validResults.every((r) => r.projectId === firstProjectId)

      if (!allSameProject) {
        errors.push('All sessions must belong to the same project')
        // Mark all as invalid if project mismatch
        sessionIds.forEach((sid) => {
          const result = results.get(sid)
          if (result) {
            results.set(sid, {
              ...result,
              isValid: false,
              error: 'Sessions belong to different projects'
            })
          }
        })
      } else if (firstProjectId) {
        setProjectId(firstProjectId)
      }
    }

    return results
  }

  const checkSession = async (): Promise<void> => {
    if (!sessionId) {
      cogoToast.error('Please enter a session ID first')
      return
    }

    setIsValidatingSession(true)
    setSessionValidation(null)
    setProjectId('')

    try {
      const result = await validateSingleSession(sessionId)
      setSessionValidation(result)

      if (result.isValid) {
        if (result.projectId) {
          setProjectId(result.projectId)
        }
        // For unrestricted users, validation is skipped but still show success
        if (isUnrestrictedUser) {
          cogoToast.success('Session ready for upload (validation skipped)')
        } else {
          cogoToast.success('Session validated successfully')
        }
      } else {
        cogoToast.error(result.error || 'Session validation failed')
      }
    } catch (error) {
      console.error('Error checking session:', error)
      cogoToast.error('Failed to check session')
      setSessionValidation({ isValid: false, error: 'Failed to check session' })
    } finally {
      setIsValidatingSession(false)
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files).filter((file) => {
        const valid = isValidFileType(file)
        if (!valid) {
          cogoToast.error(`Invalid file type: ${file.name}. Only images and videos are allowed.`)
        }
        return valid
      })

      if (files.length === 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      // Check file count limits (skip for unrestricted users)
      if (!isUnrestrictedUser) {
        const maxFiles = multipleSessionsMode ? MAX_FILES_MULTIPLE_SESSIONS : MAX_FILES_SINGLE_SESSION
        if (files.length > maxFiles) {
          cogoToast.error(
            `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed in ${multipleSessionsMode ? 'Multiple Sessions' : 'Single Session'} mode`
          )
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
          return
        }
      }

      setSelectedFiles(files)
      setValidatedSessions(new Map())
      setProjectId('')

      if (multipleSessionsMode) {
        // Extract all unique session IDs from filenames
        const sessionIds = new Set<string>()
        files.forEach((file) => {
          const extractedId = extractSessionIdFromFilename(file.name)
          if (extractedId) {
            sessionIds.add(extractedId)
          }
        })

        if (sessionIds.size === 0) {
          cogoToast.error(
            'No session IDs found in filenames. Filenames should contain "dreambooth-{sessionId}"'
          )
          return
        }

        // Validate all sessions (skip validation errors for unrestricted users)
        setIsValidatingSession(true)
        try {
          const results = await validateMultipleSessions(Array.from(sessionIds))
          setValidatedSessions(results)

          const allValid = Array.from(results.values()).every((r) => r.isValid)
          if (allValid && results.size > 0) {
            const firstResult = Array.from(results.values())[0]
            if (firstResult.projectId) {
              setProjectId(firstResult.projectId)
            }
            if (!isUnrestrictedUser) {
              cogoToast.success(`Validated ${results.size} session${results.size > 1 ? 's' : ''}`)
            }
          } else if (!isUnrestrictedUser) {
            const errors = Array.from(results.values())
              .filter((r) => !r.isValid)
              .map((r) => r.error || 'Invalid session')
            cogoToast.error(`Some sessions failed validation: ${errors.join(', ')}`)
          }
        } catch (error) {
          console.error('Error validating sessions:', error)
          if (!isUnrestrictedUser) {
            cogoToast.error('Failed to validate sessions')
          }
        } finally {
          setIsValidatingSession(false)
        }
      } else {
        // In single session mode, if session ID is already entered and validated, keep it
        // Otherwise, clear validation state
        if (!sessionId) {
          setSessionValidation(null)
        }
      }
    }
  }

  const openGallery = (sessionIdToOpen: string): void => {
    const galleryUrl = resolveGallerySessionUrlFromStore(sessionIdToOpen)
    void openExternalUrl(galleryUrl)
  }

  const handleUpload = async (): Promise<void> => {
    // Validation checks
    if (!selectedFiles.length) {
      cogoToast.error('Please select files to upload.')
      return
    }

    if (multipleSessionsMode) {
      // Check if all sessions are validated (skip for unrestricted users)
      if (!isUnrestrictedUser) {
        if (
          validatedSessions.size === 0 ||
          Array.from(validatedSessions.values()).some((r) => !r.isValid)
        ) {
          cogoToast.error('Please ensure all sessions are validated before uploading.')
          return
        }
        if (!projectId) {
          cogoToast.error('Project ID not found. Please validate sessions again.')
          return
        }
      }
    } else {
      // Single session mode
      if (!sessionId) {
        cogoToast.error('Please enter a session ID.')
        return
      }
      // Skip validation check for unrestricted users
      if (!isUnrestrictedUser && (!sessionValidation?.isValid || !projectId)) {
        cogoToast.error('Please validate the session before uploading.')
        return
      }
      if (!isUnrestrictedUser && selectedFiles.length > MAX_FILES_SINGLE_SESSION) {
        cogoToast.error(`Maximum ${MAX_FILES_SINGLE_SESSION} files allowed in Single Session mode.`)
        return
      }
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      if (multipleSessionsMode) {
        // Multiple sessions upload
        const totalFiles = selectedFiles.length
        let successfulUploads = 0
        const uploadedSessionIds = new Set<string>()

        for (let i = 0; i < totalFiles; i++) {
          const file = selectedFiles[i]
          const fileSessionId = extractSessionIdFromFilename(file.name)

          if (!fileSessionId) {
            cogoToast.error(`No session ID found in filename: ${file.name}`)
            continue
          }

          // Verify this session is validated (skip for unrestricted users)
          if (!isUnrestrictedUser) {
            const validation = validatedSessions.get(fileSessionId)
            if (!validation?.isValid) {
              cogoToast.error(`Session ${fileSessionId} is not validated: ${file.name}`)
              continue
            }
          }

          try {
            // For unrestricted users, use projectId if available, otherwise use empty string
            // Empty string will be treated as "no projectId" by the backend
            const uploadProjectId = isUnrestrictedUser && !projectId ? '' : projectId
            const { promise } = await uploadManager.addTask(
              file,
              user.email,
              uploadProjectId,
              fileSessionId,
              `gallery-${fileSessionId}`,
              getFileType(file)
            )

            await promise
            successfulUploads++
            uploadedSessionIds.add(fileSessionId)
            cogoToast.success(`Uploaded: ${file.name}`)
          } catch (error) {
            console.error(`Error uploading ${file.name}:`, error)
            cogoToast.error(`Failed to upload ${file.name}`)
          }

          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100))
        }

        cogoToast.success(
          `Batch upload complete: ${successfulUploads}/${totalFiles} files uploaded`
        )

        // Open the gallery for the first session ID if any uploads succeeded
        if (uploadedSessionIds.size > 0) {
          openGallery(Array.from(uploadedSessionIds)[0])
        }
      } else {
        // Single session upload
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i]
          try {
            // For unrestricted users, use projectId if available, otherwise use empty string
            // Empty string will be treated as "no projectId" by the backend
            const uploadProjectId = isUnrestrictedUser && !projectId ? '' : projectId
            const { promise } = await uploadManager.addTask(
              file,
              user.email,
              uploadProjectId,
              sessionId,
              `gallery-${sessionId}`,
              getFileType(file)
            )

            await promise
            setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100))
          } catch (error) {
            console.error(`Error uploading ${file.name}:`, error)
            cogoToast.error(`Failed to upload ${file.name}`)
            throw error
          }
        }

        cogoToast.success(
          `Successfully uploaded ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`
        )
        openGallery(sessionId)
      }
    } catch (error) {
      console.error('Upload failed:', error)
      cogoToast.error('Upload failed. Please try again.')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setSelectedFiles([])
      setSessionValidation(null)
      setValidatedSessions(new Map())
      setProjectId('')
      setSessionId('')
    }
  }

  return (
    <div className="w-full min-h-screen flex items-center justify-center p-4 bg-whiteout">
      <div className="flex flex-col items-center gap-6 bg-white p-8 shadow-lg rounded-xl w-full max-w-md mx-auto">
        <h1 className="text-2xl font-bold text-carbon mb-2">Upload File</h1>

        <div className="flex flex-col items-stretch gap-4 w-full">
          {/* Mode Toggle */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Mode:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setMultipleSessionsMode(false)
                    setSelectedFiles([])
                    setSessionValidation(null)
                    setValidatedSessions(new Map())
                    setProjectId('')
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${!multipleSessionsMode ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Single Session
                </button>
                <button
                  onClick={() => {
                    setMultipleSessionsMode(true)
                    setSessionId('')
                    setSessionValidation(null)
                    setSelectedFiles([])
                    setValidatedSessions(new Map())
                    setProjectId('')
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${multipleSessionsMode ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Multiple Sessions
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {isUnrestrictedUser
                ? multipleSessionsMode
                  ? 'Unlimited uploads. Session IDs are detected from filenames (format: dreambooth-{sessionId}).'
                  : 'Unlimited uploads to an existing session.'
                : multipleSessionsMode
                  ? 'Upload up to 50 photos. Session IDs are detected from filenames (format: dreambooth-{sessionId}).'
                  : 'Upload up to 10 photos to an existing session.'}
            </p>
          </div>

          {/* Session ID (only in single session mode) */}
          {!multipleSessionsMode && (
            <div className="flex flex-col gap-1">
              <label className="block text-sm font-medium text-gray-700">Session ID</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={sessionId}
                  onChange={(e) => {
                    setSessionId(e.target.value)
                    setSessionValidation(null)
                    setProjectId('')
                  }}
                  className="flex-1 p-2 border rounded-md text-sm"
                  placeholder="Enter Session ID"
                />
                <Button
                  text={isValidatingSession ? 'Validating...' : 'Validate'}
                  onClick={checkSession}
                  disabled={isValidatingSession || !sessionId}
                  variant="secondary"
                  className="!text-sm py-2 px-3"
                />
              </div>
              {sessionValidation && (
                <p
                  className={`text-sm ${sessionValidation.isValid ? 'text-green-600' : 'text-red-600'}`}
                >
                  {sessionValidation.isValid ? (
                    <>
                      ✓ Session validated
                      {projectId && (
                        <span className="ml-2 text-gray-500">(Project: {projectId})</span>
                      )}
                    </>
                  ) : (
                    `✗ ${sessionValidation.error || 'Validation failed'}`
                  )}
                </p>
              )}
            </div>
          )}

          {/* File Upload */}
          <div className="flex flex-col gap-1">
            <label className="block text-sm font-medium text-gray-700">
              Files{' '}
              {isUnrestrictedUser
                ? '(unlimited)'
                : multipleSessionsMode
                  ? `(max ${MAX_FILES_MULTIPLE_SESSIONS})`
                  : `(max ${MAX_FILES_SINGLE_SESSION})`}
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full p-2 border rounded-md text-sm"
              aria-label="Select files to upload"
              title="Select files to upload"
              accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES].join(',')}
              multiple={true}
              ref={fileInputRef}
            />
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: Images (.jpg, .png, .gif) and Videos (.mp4, .mov, .avi)
            </p>
            {selectedFiles.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Selected: {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Multiple Sessions Validation Results */}
          {multipleSessionsMode && validatedSessions.size > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-gray-700">Session Validation:</p>
              <div className="max-h-32 overflow-y-auto border rounded-md p-2 text-xs">
                {Array.from(validatedSessions.entries()).map(([sid, result]) => (
                  <div
                    key={sid}
                    className={`py-1 ${result.isValid ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {result.isValid ? '✓' : '✗'} {sid}:{' '}
                    {result.isValid ? 'Valid' : result.error || 'Invalid'}
                  </div>
                ))}
              </div>
              {projectId && <p className="text-xs text-gray-600 mt-1">Project: {projectId}</p>}
            </div>
          )}

          {/* Upload Button */}
          <Button
            text={
              isUploading
                ? `Uploading${uploadProgress > 0 ? ` ${uploadProgress}%` : '...'}`
                : 'Upload'
            }
            onClick={handleUpload}
            disabled={
              isUploading ||
              !selectedFiles.length ||
              isValidatingSession ||
              (!multipleSessionsMode && !sessionId) ||
              (!isUnrestrictedUser && (
                !projectId ||
                (multipleSessionsMode &&
                  (validatedSessions.size === 0 ||
                    Array.from(validatedSessions.values()).some((r) => !r.isValid))) ||
                (!multipleSessionsMode && !sessionValidation?.isValid)
              ))
            }
            variant="primary"
            className="!text-sm py-2 w-full mt-2"
          />
        </div>
      </div>
    </div>
  )
}

export default UploadPage
